import { Injectable } from '@angular/core';
import {
  NavigationEnd,
  Params,
  Router
} from '@angular/router';
import { of, Observable, shareReplay } from "rxjs";
import { catchError, tap, filter, map, switchMap, distinctUntilChanged } from 'rxjs';
import { PmaEntityKeyHttpService } from '@feature/pma-entity/services/pma-entity-key-http.service';
import { Store, select } from '@ngrx/store';
import { ApplicationState } from '@feature/store/application-store';
import { emptyEntityKey } from '@feature/pma-entity/pma-entity-types';
import _ from 'lodash';
import { EntityKeyUpdated } from '../actions/entity-key.actions';
import { EntityKey } from '@feature/pma-entity/pma-entity-types';
import * as entityKeySelectors from '@feature/pma-entity/selectors/entity-key';
import { NGXLogger } from 'ngx-logger';
import { collectRouteParams } from '@feature/routing/route-util';
import {MojoRouterEventsService} from "@routing/mojo-router-events.service";

@Injectable({
  providedIn: 'root',
})
export class EntityKeyService {

  constructor(
    private readonly logger: NGXLogger,
    private readonly router: Router,
    private readonly entityKeyHttpService: PmaEntityKeyHttpService,
    private readonly store: Store<ApplicationState>,
    private readonly routerEvents: MojoRouterEventsService) {

    const hasParameters = (p: Params) => p && Object.keys(p).length > 0;

    const getKeyOrEmptyKey = (p: Params): Observable<EntityKey> => hasParameters(p)
      ? this.entityKeyHttpService.getKey(p).pipe(map(x => x?.Key))
      : of(emptyEntityKey);

    this.routerEvents.NavigationEnd$.pipe(
      filter((e: NavigationEnd) => {
        // do not do entity key lookups for the responsive apps
        return !e.url.startsWith('/m/') ;
      }),
      map(() => this.collectRouteParams(this.router)),
      distinctUntilChanged((a, b) => _.isEqual(a, b)),
      switchMap(routeParams => getKeyOrEmptyKey(routeParams)),
      tap(entityKey => this.updateKeyInStore(entityKey)),
      catchError((e) => {
        this.logger.error(`EntityKeyService error: ${e}`);
        return of(null);
      })
    ).subscribe();
  }

  getEntityKey(): Observable<EntityKey> {
    return this.store.pipe(
      select(entityKeySelectors.selectEntityKey),
      distinctUntilChanged(),
      shareReplay()
    );
  }

  getEntityKeyTypeName(): Observable<string> {
    return this.getEntityKey().pipe(
      map(x => x.EntityTypeName),
      shareReplay()
    );
  }

  collectRouteParams(router: Router): Object {
    return collectRouteParams(router);
  }

  setEmptyEntityKey(): void {
    this.updateKeyInStore(emptyEntityKey);
  }

  private updateKeyInStore(key: EntityKey){
    const entityKey = key ?? emptyEntityKey;
    const keyAction = EntityKeyUpdated({ entityKey: entityKey });
    this.store.dispatch(keyAction);
  }
}
